import {
  type Block,
  type BlockStyle,
  type BlockType,
  type Device,
  type EditorMode,
  newBlockId,
  type Page,
  PageMetaSchema,
  PageSchema,
  SlugSchema,
  setPath,
} from "@aurumiq/cms-schema";
import { toast } from "@aurumiq/ui/components/sonner";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CmsPageDetail, CmsPageKind } from "@/lib/types";
import { type BlockValidation, validateBlock } from "../editor/blocks";
import { useCmsPage, usePublishPage, useRevertPage, useSaveDraft } from "../queries";
import { makeBlock } from "./defaults";

/** A block as held by the designer: raw JSON with a guaranteed id/type (may fail the schema). */
export type DesignerBlock = Record<string, unknown> & { id: string; type: string };

/** PageSchema-shaped local state; blocks stay raw so invalid ones can be shown and fixed. */
export interface DesignerPageState {
  slug: string;
  kind: CmsPageKind;
  title: string;
  excerpt: string;
  blocks: DesignerBlock[];
  meta: Record<string, unknown>;
}

const HISTORY_CAP = 100;
/** Rapid edits of the same key (inline typing) collapse into one undo step. */
export const COALESCE_MS = 800;

function normaliseBlocks(raw: unknown[]): DesignerBlock[] {
  return raw.map((b, i) => {
    const obj = typeof b === "object" && b !== null ? (b as Record<string, unknown>) : { value: b };
    const type = typeof obj.type === "string" ? obj.type : "unknown";
    const id = typeof obj.id === "string" && obj.id ? obj.id : newBlockId(`${type}-${i}`);
    const parsed = validateBlock({ ...obj, id });
    // Valid blocks are stored parsed so schema defaults (style, hidden, …) are explicit.
    return (parsed.ok ? parsed.block : { ...obj, id, type }) as DesignerBlock;
  });
}

function fromDetail(d: CmsPageDetail): DesignerPageState {
  return {
    slug: d.slug,
    kind: d.kind,
    title: d.title,
    excerpt: d.excerpt ?? "",
    blocks: normaliseBlocks(d.draft.blocks),
    meta: d.draft.meta ?? {},
  };
}

export function useDesignerState(pageId: string) {
  const pageQ = useCmsPage(pageId);
  const saveM = useSaveDraft(pageId);
  const publishM = usePublishPage(pageId);
  const revertM = useRevertPage(pageId);

  const [detail, setDetail] = useState<CmsPageDetail | null>(null);
  const [page, setPage] = useState<DesignerPageState | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [mode, setMode] = useState<EditorMode>("edit");
  const [device, setDevice] = useState<Device>("desktop");
  const [dirty, setDirty] = useState(false);

  const undoStack = useRef<DesignerPageState[]>([]);
  const redoStack = useRef<DesignerPageState[]>([]);
  const lastPush = useRef<{ at: number; key: string | null }>({ at: 0, key: null });
  const [, force] = useState(0);
  const rerender = useCallback(() => force((n) => n + 1), []);

  // Initial load only; later server states are adopted from mutation results.
  useEffect(() => {
    if (!pageQ.data || page) return;
    setDetail(pageQ.data);
    setPage(fromDetail(pageQ.data));
  }, [pageQ.data, page]);

  const stateRef = useRef<DesignerPageState | null>(null);
  stateRef.current = page;

  const pushHistory = useCallback(
    (key: string | null) => {
      const cur = stateRef.current;
      if (!cur) return;
      const now = Date.now();
      if (key && key === lastPush.current.key && now - lastPush.current.at < COALESCE_MS) {
        lastPush.current.at = now;
        return;
      }
      undoStack.current.push(cur);
      if (undoStack.current.length > HISTORY_CAP) undoStack.current.shift();
      redoStack.current = [];
      lastPush.current = { at: now, key };
      rerender();
    },
    [rerender],
  );

  const commit = useCallback(
    (next: DesignerPageState, key: string | null = null) => {
      pushHistory(key);
      setPage(next);
      stateRef.current = next;
      setDirty(true);
    },
    [pushHistory],
  );

  const undo = useCallback(() => {
    const cur = stateRef.current;
    const prev = undoStack.current.pop();
    if (!prev || !cur) return;
    redoStack.current.push(cur);
    setPage(prev);
    stateRef.current = prev;
    setDirty(true);
    lastPush.current = { at: 0, key: null };
    rerender();
  }, [rerender]);

  const redo = useCallback(() => {
    const cur = stateRef.current;
    const next = redoStack.current.pop();
    if (!next || !cur) return;
    undoStack.current.push(cur);
    setPage(next);
    stateRef.current = next;
    setDirty(true);
    lastPush.current = { at: 0, key: null };
    rerender();
  }, [rerender]);

  /** Replace local state with what the server returned (save / publish / revert). */
  const adopt = useCallback(
    (d: CmsPageDetail) => {
      setDetail(d);
      const next = fromDetail(d);
      setPage(next);
      stateRef.current = next;
      undoStack.current = [];
      redoStack.current = [];
      lastPush.current = { at: 0, key: null };
      setDirty(false);
      setSelectedId((sel) => (sel && next.blocks.some((b) => b.id === sel) ? sel : null));
      rerender();
    },
    [rerender],
  );

  /* ---------- block mutations ---------- */

  const mapBlocks = useCallback(
    (fn: (blocks: DesignerBlock[]) => DesignerBlock[], key: string | null = null) => {
      const cur = stateRef.current;
      if (!cur) return;
      commit({ ...cur, blocks: fn(cur.blocks) }, key);
    },
    [commit],
  );

  const setField = useCallback(
    (id: string, path: string, value: unknown) =>
      mapBlocks(
        (bs) => bs.map((b) => (b.id === id ? setPath(b, path, value) : b)),
        `${id}:${path}`,
      ),
    [mapBlocks],
  );

  const setBlock = useCallback(
    (id: string, block: Record<string, unknown>, key: string | null = null) =>
      mapBlocks(
        (bs) => bs.map((b) => (b.id === id ? ({ ...block, id } as DesignerBlock) : b)),
        key,
      ),
    [mapBlocks],
  );

  const setStyle = useCallback(
    (id: string, patch: Partial<BlockStyle>) =>
      mapBlocks(
        (bs) =>
          bs.map((b) =>
            b.id === id
              ? { ...b, style: { ...((b.style as Record<string, unknown>) ?? {}), ...patch } }
              : b,
          ),
        `${id}:style`,
      ),
    [mapBlocks],
  );

  const moveBlock = useCallback(
    (id: string, toIndex: number) =>
      mapBlocks((bs) => {
        const from = bs.findIndex((b) => b.id === id);
        if (from < 0) return bs;
        const arr = bs.slice();
        const [it] = arr.splice(from, 1) as [DesignerBlock];
        arr.splice(Math.max(0, Math.min(toIndex, arr.length)), 0, it);
        return arr;
      }),
    [mapBlocks],
  );

  const moveBy = useCallback(
    (id: string, delta: 1 | -1) => {
      const cur = stateRef.current;
      if (!cur) return;
      const from = cur.blocks.findIndex((b) => b.id === id);
      const to = from + delta;
      if (from < 0 || to < 0 || to >= cur.blocks.length) return;
      moveBlock(id, to);
    },
    [moveBlock],
  );

  /** Full reorder (from the Layers drag list). */
  const reorderBlocks = useCallback(
    (ids: string[]) =>
      mapBlocks((bs) => {
        const byId = new Map(bs.map((b) => [b.id, b]));
        const ordered = ids.map((i) => byId.get(i)).filter((b): b is DesignerBlock => !!b);
        return [...ordered, ...bs.filter((b) => !ids.includes(b.id))];
      }),
    [mapBlocks],
  );

  const insertBlock = useCallback(
    (type: BlockType, index?: number): Block => {
      const block = makeBlock(type);
      mapBlocks((bs) => {
        const arr = bs.slice();
        const at = index == null || index < 0 || index > arr.length ? arr.length : index;
        arr.splice(at, 0, block as DesignerBlock);
        return arr;
      });
      setSelectedId(block.id);
      return block;
    },
    [mapBlocks],
  );

  const duplicateBlock = useCallback(
    (id: string) => {
      const cur = stateRef.current;
      const i = cur?.blocks.findIndex((b) => b.id === id) ?? -1;
      const src = cur?.blocks[i];
      if (!cur || !src) return;
      const copy = { ...structuredClone(src), id: newBlockId(src.type) } as DesignerBlock;
      if (typeof src.label === "string" && src.label) copy.label = `${src.label} (copy)`;
      mapBlocks((bs) => [...bs.slice(0, i + 1), copy, ...bs.slice(i + 1)]);
      setSelectedId(copy.id);
    },
    [mapBlocks],
  );

  const deleteBlock = useCallback(
    (id: string) => {
      mapBlocks((bs) => bs.filter((b) => b.id !== id));
      setSelectedId((sel) => (sel === id ? null : sel));
    },
    [mapBlocks],
  );

  const toggleHidden = useCallback(
    (id: string) =>
      mapBlocks((bs) => bs.map((b) => (b.id === id ? { ...b, hidden: b.hidden !== true } : b))),
    [mapBlocks],
  );

  /* ---------- page mutations ---------- */

  const setMeta = useCallback(
    (meta: Record<string, unknown>) => {
      const cur = stateRef.current;
      if (cur) commit({ ...cur, meta }, "meta");
    },
    [commit],
  );
  const setTitle = useCallback(
    (title: string) => {
      const cur = stateRef.current;
      if (cur) commit({ ...cur, title }, "title");
    },
    [commit],
  );
  const setSlug = useCallback(
    (slug: string) => {
      const cur = stateRef.current;
      if (cur) commit({ ...cur, slug }, "slug");
    },
    [commit],
  );
  const setExcerpt = useCallback(
    (excerpt: string) => {
      const cur = stateRef.current;
      if (cur) commit({ ...cur, excerpt }, "excerpt");
    },
    [commit],
  );

  /* ---------- derived ---------- */

  const validations = useMemo<BlockValidation[]>(
    () => (page?.blocks ?? []).map((b) => validateBlock(b)),
    [page?.blocks],
  );
  const invalidCount = validations.filter((v) => !v.ok).length;
  const metaParsed = useMemo(() => PageMetaSchema.safeParse(page?.meta ?? {}), [page?.meta]);

  /** Schema-valid page for the canvas (invalid blocks dropped, last good slug kept). */
  const previewPage = useMemo<Page | null>(() => {
    if (!page) return null;
    const r = PageSchema.safeParse({
      slug: SlugSchema.safeParse(page.slug).success ? page.slug : (detail?.slug ?? "home"),
      kind: page.kind,
      title: page.title || "Untitled",
      excerpt: page.excerpt || undefined,
      blocks: validations.filter((v) => v.ok).map((v) => v.block),
      meta: metaParsed.success ? metaParsed.data : undefined,
    });
    return r.success ? r.data : null;
  }, [page, validations, metaParsed, detail?.slug]);

  const selected = useMemo(
    () => page?.blocks.find((b) => b.id === selectedId) ?? null,
    [page?.blocks, selectedId],
  );

  /* ---------- persistence ---------- */

  const save = useCallback(async (): Promise<CmsPageDetail | null> => {
    const cur = stateRef.current;
    if (!cur || !detail) return null;
    const vals = cur.blocks.map((b) => validateBlock(b));
    const bad = vals.filter((v) => !v.ok).length;
    if (bad > 0) {
      toast.error(`Fix or delete ${bad} invalid block${bad > 1 ? "s" : ""} before saving`);
      return null;
    }
    const meta = PageMetaSchema.safeParse(cur.meta);
    if (!meta.success) {
      toast.error("SEO fields are invalid");
      return null;
    }
    if (!SlugSchema.safeParse(cur.slug).success) {
      toast.error("The slug is invalid");
      return null;
    }
    const res = await saveM.mutateAsync({
      blocks: vals.map((v) => v.block),
      meta: meta.data,
      title: cur.title !== detail.title ? cur.title : undefined,
      slug: cur.slug !== detail.slug ? cur.slug : undefined,
      excerpt: cur.excerpt !== (detail.excerpt ?? "") ? cur.excerpt : undefined,
      note: "designer",
    });
    adopt(res);
    return res;
  }, [detail, saveM, adopt]);

  /** Save (when dirty) then publish that revision. */
  const publish = useCallback(
    async (reason?: string) => {
      let revisionId = detail?.draft.revision_id;
      if (dirty || !revisionId) {
        const saved = await save();
        if (!saved) throw new Error("Save failed");
        revisionId = saved.draft.revision_id;
      }
      const res = await publishM.mutateAsync({ revision_id: revisionId, reason });
      adopt(res);
      return res;
    },
    [detail, dirty, save, publishM, adopt],
  );

  const revert = useCallback(
    async (revisionId: string) => {
      const res = await revertM.mutateAsync(revisionId);
      adopt(res);
      return res;
    },
    [revertM, adopt],
  );

  return {
    loading: pageQ.isPending || (!page && !pageQ.isError),
    error: pageQ.error,
    detail,
    page,
    previewPage,
    validations,
    invalidCount,
    metaParsed,
    selectedId,
    selected,
    hoveredId,
    mode,
    device,
    dirty,
    saving: saveM.isPending,
    publishing: publishM.isPending,
    reverting: revertM.isPending,
    canUndo: undoStack.current.length > 0,
    canRedo: redoStack.current.length > 0,
    setSelectedId,
    setHoveredId,
    setMode,
    setDevice,
    undo,
    redo,
    setField,
    setBlock,
    setStyle,
    moveBlock,
    moveBy,
    reorderBlocks,
    insertBlock,
    duplicateBlock,
    deleteBlock,
    toggleHidden,
    setMeta,
    setTitle,
    setSlug,
    setExcerpt,
    save,
    publish,
    revert,
  };
}

export type DesignerState = ReturnType<typeof useDesignerState>;
